import React, { useMemo, useState } from "react";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { Autocomplete } from "@react-google-maps/api";
import Select from "react-select";
import TRUCK_TYPES from "../../assets/truck_types.js";

const BasicInfo = () => {
  const { onboardingProfile, setOnboardingProfile, error, setError } = useOnboarding();
  const [originAutocomplete, setOriginAutocomplete] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOnboardingProfile({ [name]: value });
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  const truckTypeOptions = useMemo(
    () =>
      TRUCK_TYPES.map((type) => ({
        value: type.value,
        label: `${type.label} (${type.value})`,
      })),
    []
  );

  const handleOriginPlaceChanged = () => {
    if (!originAutocomplete) return;
    const place = originAutocomplete.getPlace();
    if (!place) return;
    const cityComponent = place.address_components?.find((component) =>
      component.types.includes("locality")
    );
    if (cityComponent) {
      setOnboardingProfile({ origin_city: cityComponent.long_name });
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={onboardingProfile.phone_number || ""}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-tw-primary focus:border-transparent ${error && (error.includes("phone") || error.includes("Phone"))
              ? "border-red-300"
              : "border-gray-300"
              }`}
            placeholder="Enter phone number"
            required
            minLength={10}
            maxLength={10}
            pattern="\d{10}"
          />
          {error && (error.includes("phone") || error.includes("Phone")) }
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Truck Type <span className="text-red-500">*</span>
          </label>
          <Select
            options={truckTypeOptions}
            value={
              onboardingProfile.truck_type
                ? truckTypeOptions.find(
                    (opt) => opt.value === onboardingProfile.truck_type
                  )
                : null
            }
            onChange={(opt) => {
              setOnboardingProfile({ truck_type: opt?.value || "" });
              if (error) {
                setError("");
              }
            }}
            placeholder="Select truck type"
            isClearable={false}
            classNamePrefix="react-select"
            styles={{
              control: (base, state) => ({
                ...base,
                backgroundColor: "#ffffff",
                borderColor:
                  error && error.toLowerCase().includes("truck")
                    ? "#ef4444"
                    : state.isFocused
                      ? "#1E3A5F"
                      : "#d1d5db",
                boxShadow: state.isFocused
                  ? "0 0 0 2px rgba(30,58,95,0.25)"
                  : "none",
                "&:hover": { borderColor: "#94a3b8" },
                minHeight: "46px",
              }),
              menu: (base) => ({
                ...base,
                zIndex: 20,
                backgroundColor: "#ffffff",
                color: "#0f172a",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#1E3A5F"
                  : state.isFocused
                    ? "#f1f5f9"
                    : "#ffffff",
                color: state.isSelected ? "#ffffff" : "#0f172a",
                cursor: "pointer",
              }),
              singleValue: (base) => ({
                ...base,
                color: "#0f172a",
              }),
              placeholder: (base) => ({
                ...base,
                color: "#64748b",
              }),
              input: (base) => ({
                ...base,
                color: "#0f172a",
              }),
            }}
          />
          {error && error.toLowerCase().includes("truck") && (
            <p className="mt-1 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div>
          <label htmlFor="origin_city" className="block text-sm font-medium text-gray-700 mb-2">
            Origin City (optional)
          </label>
          <Autocomplete
            onLoad={(instance) => setOriginAutocomplete(instance)}
            onPlaceChanged={handleOriginPlaceChanged}
            restrictions={{ country: "us" }}
          >
            <input
              type="text"
              id="origin_city"
              name="origin_city"
              value={onboardingProfile.origin_city || ""}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-tw-primary focus:border-transparent"
              placeholder="Start typing a city"
            />
          </Autocomplete>
          <p className="mt-1 text-sm text-gray-500">
            Optional. Helps personalize load recommendations near you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BasicInfo;

